import type { NonCancelableCustomEvent } from '@risk-smart/themed-cloudscape-components/interfaces';
import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo } from 'react';

import type { TablePropsWithActions, TableRecord } from '../types';

/**
 * Wraps `tableProps.onSortingChange` so a ref stays in sync with the table's
 * live sort direction. Comparators that pin sentinel rows (`-` / `—` / `N/A`)
 * to the BOTTOM in both directions read this ref — see `compareNullableNumber`
 * in `@/utils/numberUtils`.
 *
 * The ref MUST be updated synchronously before the wrapped handler fires
 * `setSortingState`, so it is guaranteed current by the time Cloudscape
 * re-runs the comparator on the next render.
 */
export const useSortingDirectionTracker = <T extends TableRecord>(
  tableProps: TablePropsWithActions<T>,
  sortingDescendingRef: MutableRefObject<boolean>
): TablePropsWithActions<T> => {
  const { onSortingChange: original } = tableProps;
  const onSortingChange = useCallback(
    (event: NonCancelableCustomEvent<TableProps.SortingState<T>>) => {
      sortingDescendingRef.current = event.detail.isDescending ?? false;
      original?.(event);
    },
    [original, sortingDescendingRef]
  );

  return useMemo(
    () => ({ ...tableProps, onSortingChange }),
    [tableProps, onSortingChange]
  );
};
